import { Button, message } from "antd";
import { useEffect, useRef, useState } from "react";
import { MenuFolderDto } from "../../generated/dashboard-client";
import DesignApi from "../../api/DesignApi";
import "./MoveToFolderModal.scss";
import { DownOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { FolderThumb } from "../pages/Designs";
import FolderIcon from "../icons/FolderIcon";
import { CreateFolderContent } from "./CreateFolderContent";

export interface MoveToFolderMenuContentProps {
  ids: string[];
  folders: MenuFolderDto[];
  activeFolder: MenuFolderDto | null;
  onCloseMovedToFolder: (save: boolean, folder?: MenuFolderDto) => void;
  onCloseMovedToSavedDesigns: (designIds: string[]) => void;
  onCloseMovedFolderToFolder: (childFolderId: number, parentFolderId: string) => void;
  folderToMove: MenuFolderDto | null;
  folderThumbs: FolderThumb[];
  folderNameExists: (folder: MenuFolderDto | null, name: string) => boolean | undefined;
  onCreateFolderModalClose: (ids: string[], folderToMove?: MenuFolderDto | null, folder?: MenuFolderDto) => void;
  onBack: () => void;
  isMobile: boolean;
}

export function MoveToFolderMenuContent({
  ids,
  folders,
  activeFolder,
  onCloseMovedToFolder,
  onCloseMovedToSavedDesigns,
  onCloseMovedFolderToFolder,
  onCreateFolderModalClose,
  folderToMove,
  folderThumbs,
  folderNameExists,
  onBack,
  isMobile
}: MoveToFolderMenuContentProps) {
  const ref = useRef(null);
  const [folder, setFolder] = useState(undefined as MenuFolderDto | undefined);
  const [savedDesignsSelected, setSavedDesignsSelected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [shownFolders, setShownFolders] = useState([] as MenuFolderDto[]);
  const [isTopLevelFolder, setIsTopLevelFolders] = useState(true);
  const [mode, setMode] = useState('selectFolder');

  useEffect(() => {
    setMode('selectFolder');
    setFolder(undefined);
    let newShownFolders = folders.filter(fol => {
      if (folderToMove && fol.id === folderToMove.id) return false;
      return !fol.parentId;
    });
    setShownFolders([...newShownFolders]);
    setIsTopLevelFolders(true);
  }, [folders, activeFolder, ids, folderToMove]);

  function save() {
    if (!folder && !savedDesignsSelected) return;
    const id = folder?.id!;

    setSaving(true);

    if (folderToMove) {
      if (savedDesignsSelected) {
        DesignApi.removeFolderFromFolder(folderToMove.id!).then(() => {
          setSaving(false);
          onCloseMovedFolderToFolder(folderToMove.id!, "");
        })
      } else {
        if (folderToMove.id! === id) {
          message.error('Cannot move folder to itself!');
          setSaving(false);
        } else {
          DesignApi.putFolderInFolder(folderToMove.id!, id).then(() => {
            setSaving(false);
            onCloseMovedFolderToFolder(folderToMove.id!, id.toString());
          })
        }
      }
    } else {
      if (savedDesignsSelected) {
        DesignApi.removeFromFolder(ids)
        .then(() => {
          setSaving(false);
          onCloseMovedToSavedDesigns(ids);
        })
        .catch(() => {
          setSaving(false);
        });
      } else {
        DesignApi.putInFolder(ids, id)
        .then(() => {
          setSaving(false);
          onCloseMovedToFolder(
            true,
            folders.find((it) => it.id === id)
          );
        })
        .catch(() => {
          setSaving(false);
        });
      }
    }
  }

  function onFolderOptionClick(option: MenuFolderDto) {
    setSavedDesignsSelected(false);
    setFolder(option);
    let newShownFolders = folders.filter((fol) => {
      if (folderToMove && fol.id === folderToMove.id) return false;
      return fol.parentId === option.id!.toString();
    })
    setShownFolders([...newShownFolders]);
    setIsTopLevelFolders(false);
  }

  function goToParent(option: MenuFolderDto) {
    let parent = getParent(option);
    if (!parent) {
      setFolder(undefined);
      setIsTopLevelFolders(true);
      let newShownFolders = folders.filter(fol => {
        if (folderToMove && fol.id === folderToMove.id) return false;
        return !fol.parentId;
      });
      setShownFolders([...newShownFolders]);
    } else {
      onFolderOptionClick(parent);
    }
  }

  function hasChildren(option: MenuFolderDto) {
    return folders.filter(fol => fol.parentId === option?.id?.toString()).length > 0;
  }

  function getParent(option: MenuFolderDto) {
    let parent = undefined;
    folders.forEach((fol) => {
      if (fol.id === parseInt(option.parentId!)) {
        parent = fol;
      }
    })
    return parent;
  }

  function onSelectSavedDesigns() {
    setSavedDesignsSelected(true);
    setFolder(undefined);
  }

  function handleBack() {
    if (isMobile && mode === 'selectFolder') {
      onBack();
    } else {
      setMode('selectFolder');
    }
  }

  const foldersOptions = (
    <div className={"folders-options"} ref={ref}>
      <ul>
        {activeFolder && (!folder || isTopLevelFolder) && 
        <li onClick={onSelectSavedDesigns} className={savedDesignsSelected ? 'active' : ''}>
          <div className="icon"><FolderIcon/></div>
          <div className="info"><b>Saved Designs</b></div>
        </li>}
        {folder &&
        <li className={"folders-back"} onClick={() => goToParent(folder)}>
          <LeftOutlined/>
          {folder.name}
        </li>}
        {shownFolders.map((it) => (
          <li className={`${folder && folder.id === it.id ? 'active' : ''}`} onClick={() => onFolderOptionClick(it)}>
            <div className="icon">
              <FolderIcon/>
              <div 
                className={"img-holder"}
                style={{
                  width: '100%', 
                  height: '100%', 
                  borderRadius: '6px',
                  backgroundImage: `url(${folderThumbs.find(thumb => thumb.folderId === it.id!)?.thumb})`, 
                  backgroundSize: 'cover', 
                  backgroundPosition: 'center'
                }}
              />
            </div>
            <div className="info">
              <span><b>{it.name}</b></span>
              <span>{it.count} items</span>
            </div>
            {hasChildren(it) &&
            <div className="show-more">
              <RightOutlined/>
            </div>}
          </li>
        ))}
      </ul>
      {shownFolders.length === 0 &&
      <div className="empty-message">
        Move here or create a new folder.
      </div>}
    </div>
  )

  return (
    <div className="move-to-folder-menu-content">
      <header>
        {(mode === 'newFolder' || isMobile) &&
        <div className="handle-back" onClick={handleBack}>
          <LeftOutlined/>
        </div>}
        <h2>Move to folder</h2>
        {isMobile && <div className="mobile-close" onClick={() => onCloseMovedToFolder(false)}><DownOutlined/></div>}
      </header>

      {mode === 'selectFolder' && <>{foldersOptions}</>}

      {mode === 'selectFolder' && <footer>
        <div>
          <Button
            className={"add-folder"}
            type={"link"}
            onClick={() => setMode('newFolder')}
          >
            + Create new folder
          </Button>
        </div>
        <div className={"action-btns"}>
          <Button size={"large"} onClick={() => onCloseMovedToFolder(false)}>
            Cancel
          </Button>
          <Button
            size={"large"}
            className={"primary"}
            onClick={save}
            disabled={!folder && !savedDesignsSelected}
            loading={saving}
          >
            Move
          </Button>
        </div>
      </footer>}

      {mode === 'newFolder' &&
      <div className="create-folder-modal">
        <CreateFolderContent
          ids={ids}
          onClose={onCreateFolderModalClose}
          folderToMove={folderToMove}
          folderNameExists={folderNameExists}
          parentFolder={savedDesignsSelected ? null : folder ?? null}
        />
      </div>}
    </div>
  );
}
